import { useRef, type JSX, type ReactNode } from "react";
import Color from "color";
import type { IHeaderGroupParams } from "ag-grid-community";
import type { MergedCompositionRow } from "../rows";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { AutoTooltip, CircularProgressBar, Icon } from "@mdotm/shadcn/legacy/components";
import { readAtom, useAtomValue, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { chartsComputing, isTransientInvestmentStatus } from "../../../common";
import { PortfolioLabContext, type PortfolioLabInvestmentItem } from "../../../ctx";
import { spawnAumDialog } from "../../AumDialog";
import { formatCurrencyAmount } from "$root/utils/currency";
import { PaletteColors } from "$root/styles/themePalette";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { getThemeCssVars } from "$root/utils";
import { syncComposition } from "../persistence";
import { rescaleCompositionMonetaryWeights } from "../weight-edit";

type LabPortfolioGroupHeaderProps = IHeaderGroupParams<MergedCompositionRow> & {
	virtualUuid: string;
	color: string;
};

/**
 * Header status state machine — exactly one indicator at a time, by priority:
 * - "saving"      — the Update-composition commit request is in flight (menu locked).
 * - "error"       — the latest sync failed; edits are kept, next success clears it.
 * - "calculating" — the real investment is still calculating (or a commit is settling), so
 *                   Update composition is gated — but the menu (Reset/Remove) and editing stay available.
 * - "idle"        — nothing to report.
 */
type LabHeaderStatus = "idle" | "saving" | "error" | "calculating";

function resolveLabHeaderStatus(investment: PortfolioLabInvestmentItem | undefined): LabHeaderStatus {
	if (investment == null) {
		return "idle";
	}
	if (investment.compositionSaveStatus === "saving") {
		return "saving";
	}
	if (investment.compositionSaveStatus === "error") {
		return "error";
	}
	if (isTransientInvestmentStatus(investment.status) || investment.commitSettleStatus === "waiting") {
		return "calculating";
	}
	return "idle";
}

const statusContentByHeaderStatus: Record<
	Exclude<LabHeaderStatus, "idle">,
	{ qualifier: string; label: string; description: string; icon: ReactNode; color: string }
> = {
	saving: {
		qualifier: "PortfolioLab/ColumnHeader/SavingIndicator",
		label: "Saving...",
		description: "Saving the composition.",
		// The spinner's default accent is themeCSSVars.ProgressBar_fg_color — keep the label on the same var.
		icon: <CircularProgressBar value="indeterminate" outerDiameter={12} />,
		color: themeCSSVars.ProgressBar_fg_color,
	},
	error: {
		qualifier: "PortfolioLab/ColumnHeader/SyncErrorIcon",
		label: "Not saved",
		description:
			"The latest changes could not be saved. Your edits are kept — the next successful change clears this warning.",
		icon: <Icon icon="Alert" size={12} color={PaletteColors.NEON_RED} />,
		color: PaletteColors.NEON_RED,
	},
	calculating: {
		qualifier: "PortfolioLab/ColumnHeader/CalculatingIndicator",
		label: "Calculating…",
		description: "The real portfolio is calculating. Update composition is unavailable until it settles.",
		icon: <Icon icon="calulating" size={10} color={PaletteColors.YELLOW} />,
		color: PaletteColors.YELLOW,
	},
};

// While a status is active it REPLACES the header name (rendered as a tinted pill) — the
// header is too cramped to show both, and the name returns as soon as the status clears. The
// spinner of a queued compute sits after the name, so it goes with it.
function LabHeaderStatusLabel({ status }: { status: LabHeaderStatus }): JSX.Element | null {
	if (status === "idle") {
		return null;
	}
	const { qualifier, label, description, icon, color } = statusContentByHeaderStatus[status];
	// themeCSSVars entries are var() references the color lib can't parse — resolve to the computed value first.
	const resolvedColor = color.startsWith("var(") ? getThemeCssVars(color).trim() : color;
	return (
		<AutoTooltip
			position="top"
			overrideColor={resolvedColor}
			trigger={({ innerRef }) => (
				<span
					ref={innerRef}
					data-qualifier={qualifier}
					className="flex items-center min-w-0 whitespace-nowrap rounded px-1 py-0.5"
					style={{ color: resolvedColor, backgroundColor: Color(resolvedColor).alpha(0.1).string() }}
				>
					<span className="flex shrink-0 mr-[5px]">{icon}</span>
					<span className="truncate">{label}</span>
				</span>
			)}
		>
			{description}
		</AutoTooltip>
	);
}

export function LabPortfolioGroupHeader(params: LabPortfolioGroupHeaderProps): JSX.Element {
	const menuButtonRef = useRef<HTMLButtonElement>(null);
	const ctx = useMandatoryContext(PortfolioLabContext);
	const investments = useAtomValue(ctx.investments$);
	const investment = investments.find((i) => i.virtualUuid === params.virtualUuid);
	const currentAum = investment?.aum ?? null;
	const currency = investment?.currency;

	const aumLabel = currentAum != null ? formatCurrencyAmount(currentAum, currency, 0) : "Set AUM";

	const headerStatus = resolveLabHeaderStatus(investment);

	return (
		<div className="flex items-center w-full h-full" data-column-color={params.color}>
			<div className="ag-header-group-cell-label flex-1 min-w-0">
				{headerStatus === "idle" ? (
					<>
						<span className="ag-header-group-text">{params.displayName}</span>
						{investment != null && chartsComputing(investment) && (
							<AutoTooltip
								position="top"
								trigger={({ innerRef }) => (
									<span
										ref={innerRef}
										className="flex shrink-0 ml-1"
										data-qualifier="PortfolioLab/ColumnHeader/ComputingIndicator"
									>
										<CircularProgressBar value="indeterminate" outerDiameter={12} />
									</span>
								)}
							>
								Calculating charts
							</AutoTooltip>
						)}
					</>
				) : (
					<LabHeaderStatusLabel status={headerStatus} />
				)}
			</div>
			<Button
				size="x-small"
				palette="ghost"
				className="mr-2"
				data-qualifier="PortfolioLab/ColumnHeader/AumButton"
				onClick={() =>
					spawnAumDialog({
						currentAum,
						currency,
						onConfirm: (newAum) => {
							const live = readAtom(ctx.investments$).find((i) => i.virtualUuid === params.virtualUuid);
							if (!live) {
								return;
							}
							// The confirm rebases `initialComposition`/`initialAum` — snapshot them too so an undo
							// restores the previous delta/Reset baseline, exactly like Update AUM.
							ctx.undoRedo.snapshot({
								virtualUuid: params.virtualUuid,
								composition: live.currentComposition,
								aum: live.aum,
								initialComposition: live.initialComposition,
								initialAum: live.initialAum,
							});
							writeAtom(ctx.investments$, (items) => {
								const next = items.map((i): PortfolioLabInvestmentItem => {
									if (i.virtualUuid !== params.virtualUuid) {
										return i;
									}
									const rescaledComposition = rescaleCompositionMonetaryWeights(i.currentComposition, newAum);
									return {
										...i,
										aum: newAum,
										currentComposition: rescaledComposition,
										initialAum: newAum,
										initialComposition: rescaledComposition,
										baselineUnsaved: true,
									};
								});

								return next;
							});
							// The atom write above re-derives the grid's rowData; persistence goes through
							// the write lane with failure surfacing (T6). Both the new AUM label and the
							// sync-error icon repaint on their own — this header reads `investments$`.
							void syncComposition(ctx, params.virtualUuid);
						},
					})
				}
			>
				<Icon icon="Edit" size={18} />
				{aumLabel}
			</Button>

			{/*
			 * The column menu is anchored to this button, so NOTHING may remount this header while the
			 * menu is open: AG Grid polls the anchor's bounding rect and hides the menu the moment the
			 * node leaves the DOM. Two things used to, and both are gone — the shared wrapper handing AG
			 * Grid a fresh `columnTypes` object per render (AG Grid answers that by recreating every
			 * column definition; see `AgGridWithModules`), and `api.refreshHeader()`, which destroys and
			 * rebuilds every header row. This component subscribes to `investments$` itself, so it
			 * repaints from the atom and never needs an imperative refresh — don't reintroduce one.
			 */}
			<button
				ref={menuButtonRef}
				type="button"
				data-qualifier="PortfolioLab/ColumnHeader/Menu"
				className="ag-header-icon ag-header-cell-menu-button ag-header-menu-always-show disabled:opacity-40"
				disabled={headerStatus === "saving"}
				onClick={() => {
					if (menuButtonRef.current) {
						params.showColumnMenu(menuButtonRef.current);
					}
				}}
			>
				<span className="ag-icon ag-icon-menu-alt" role="presentation" unselectable="on" />
			</button>
		</div>
	);
}
